import { StyleSheet } from 'react-native-unistyles';
import Box from '@/components/Unistyles/Box';
import Icon from '@/components/Unistyles/Icon';
import Pressable from '@/components/Unistyles/Pressable';
import Text from '@/components/Unistyles/Text';

type CountStepperProps = {
  decrementAccessibilityLabel: string;
  incrementAccessibilityLabel: string;
  label: string;
  max: number;
  min?: number;
  onDecrement: () => void;
  onIncrement: () => void;
  testID: string;
  value: number;
};

export default function CountStepper({
  decrementAccessibilityLabel,
  incrementAccessibilityLabel,
  label,
  max,
  min = 0,
  onDecrement,
  onIncrement,
  testID,
  value,
}: CountStepperProps) {
  const canDecrement = value > min;
  const canIncrement = value < max;

  return (
    <Box
      direction="horizontal"
      alignItems="center"
      justifyContent="space-between"
      space="md"
    >
      <Text bold flexShrink={1}>
        {label}
      </Text>
      <Box direction="horizontal" alignItems="center" style={styles.stepper}>
        <Pressable
          accessibilityLabel={decrementAccessibilityLabel}
          accessibilityRole="button"
          disabled={!canDecrement}
          onPress={onDecrement}
          styleOverride={({ pressed }) =>
            styles.stepButton({ disabled: !canDecrement, pressed })
          }
          testID={`${testID}-decrease`}
        >
          <Icon name="remove-outline" size="lg" variant="typography" />
        </Pressable>
        <Box center style={styles.value} testID={testID}>
          <Text bold size="lg" selectable={false} style={styles.valueText}>
            {value}
          </Text>
        </Box>
        <Pressable
          accessibilityLabel={incrementAccessibilityLabel}
          accessibilityRole="button"
          disabled={!canIncrement}
          onPress={onIncrement}
          styleOverride={({ pressed }) =>
            styles.stepButton({ disabled: !canIncrement, pressed })
          }
          testID={`${testID}-increase`}
        >
          <Icon name="add-outline" size="lg" variant="typography" />
        </Pressable>
      </Box>
    </Box>
  );
}

const styles = StyleSheet.create((theme) => ({
  stepButton: ({
    disabled,
    pressed,
  }: {
    disabled: boolean;
    pressed: boolean;
  }) => ({
    alignItems: 'center',
    backgroundColor: pressed ? theme.colors.surfaceSecondary : 'transparent',
    height: theme.size(10),
    justifyContent: 'center',
    opacity: disabled ? 0.4 : 1,
    width: theme.size(8),
    _web: {
      cursor: disabled ? 'not-allowed' : 'pointer',
      _hover: disabled
        ? {}
        : {
            backgroundColor: theme.colors.surfaceSecondary,
          },
    },
  }),
  stepper: {
    backgroundColor: theme.colors.surface,
    borderColor: theme.colors.borderMuted,
    borderRadius: theme.borderRadius.lg,
    borderWidth: StyleSheet.hairlineWidth,
    height: theme.size(10),
    overflow: 'hidden',
  },
  value: {
    height: theme.size(10),
    minWidth: theme.size(12),
  },
  valueText: {
    fontVariant: ['tabular-nums'],
  },
}));
